<script>
    import { mapActions, mapGetters, mapState } from "vuex";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import { metaInfo } from "@/core/MetaInfo";
    import { apiUrl } from "@/utils";
    import {
        SIMILAR_PRODUCTS_BREAKPOINT,
        RELATED_PRODUCTS_BREAKPOINT,
    } from "@nanoleaf/settings/breakpoints";
    import TheSkeleton from "@main/packages/TheSkeleton";
    import ProductCardsSlider from "@/components/product/ProductCardsSlider";
    import ProductGallery from "@/components/product/ProductGallery";
    import SchemaForProduct from "@/components/schema/SchemaForProduct";
    import ProductFooter from "@/components/product/ProductFooter";
    import ProductBody from "@/components/product/ProductBody";
    import ProductAside from "@nanoleaf/components/ProductAside";
    import GuaranteeList from "@nanoleaf/components/GuaranteeList";

    export default {
        name: "Product",
        components: {
            GuaranteeList,
            ProductCardsSlider,
            ProductAside,
            ProductAsideEol: () =>
                import(
                    /* webpackChunkName: "nanoleaf-eol" */ "@/components/product/ProductAsideEol"
                ),
            ProductGallery,
            SchemaForProduct,
            TheSkeleton,
            ProductFooter,
            ProductBody,
        },
        data() {
            return {
                videoList: [],
                isLoading: false,
                componentKey: 0,
                mobileActiveTabForBody: "description",
            };
        },
        computed: {
            ...mapState("Product", {
                product: (state) => state.product,
                similarProductsIds: (state) => state.product?.similarProductsIds || [],
                relatedProductsIds: (state) => state.product?.relatedProductsIds || [],
                gallery: (state) => state.gallery,
                reviews: (state) => state.reviews,
            }),
            ...mapGetters("Root", ["productsInCartIds"]),
            listForHeader() {
                let arr = [];
                if (this.reviews.total) arr.push("reviews");
                if (this.similarProductsIds.length) arr.push("similar");

                return arr;
            },
            currentSlug() {
                return encodeURI(this.$route.params.slug);
            },
            breakpoints() {
                return {
                    related: RELATED_PRODUCTS_BREAKPOINT,
                    similar: SIMILAR_PRODUCTS_BREAKPOINT,
                };
            },
        },
        watch: {
            $route() {
                this.mobileActiveTabForBody = "description";
                this.getCurrentProduct();
            },
        },
        async serverPrefetch() {
            const { id, offers } = await this.getProduct({
                slug: this.currentSlug,
            });

            return this.selectedLoadGallery({ id, offers });
        },
        mounted() {
            if (!this.product) {
                this.getCurrentProduct();
            } else {
                this.getProductReview({ id: this.product.id });
                this.getProductVideo(this.product.id);
                this.sendDataLayer();
            }
        },
        destroyed() {
            this.getProduct({ isReset: true });
        },
        methods: {
            ...mapActions("Product", ["getProduct", "getProductGallery", "getProductReview"]),
            async selectedLoadGallery({ id, offers }) {
                const availableOffers = offers.filter((offer) => offer.available);
                const hasOffers = availableOffers.length > 0;
                const currentId = hasOffers ? availableOffers[0].id : id;
                const typeFilters = hasOffers ? "offersIds" : "productsIds";

                await this.getProductGallery({ type: typeFilters, id: currentId });
            },
            getCurrentProduct() {
                this.isLoading = true;
                this.getProduct({
                    slug: this.currentSlug,
                }).then(({ id, offers }) => {
                    this.isLoading = false;
                    this.getProductReview({ id });
                    this.getProductVideo(id);
                    this.selectedLoadGallery({ id, offers });
                    this.sendDataLayer();
                });
            },
            getProductVideo(id) {
                this.$store.productsApi
                    .getVideoAboutProduct({ productId: id })
                    .then(({ items }) => (this.videoList = items));
            },
            sendDataLayer() {
                const { id, name, discountPrice } = this.product.offers[0];

                sendAnalytics.send(sendAnalytics.events.productDetail, {
                    id,
                    name,
                    discountPrice,
                });
                sendAnalytics.send(sendAnalytics.events.catalogProduct);
            },
            anchorScrollHandler() {
                this.mobileActiveTabForBody = "characteristic";
            },
        },
        metaInfo() {
            return metaInfo.getProductMeta({
                title: this.product?.metaTitle || "",
                description: this.product?.metaDescription || "",
                slug: `p/${this.currentSlug}`,
                imageUrl: apiUrl(this.product?.previewImage.medium || ""),
            });
        },
    };
</script>

<template>
    <div class="product">
        <template v-if="product">
            <div class="l-container">
                <div class="product__head" itemscope itemtype="http://schema.org/Product">
                    <schema-for-product :product="product"></schema-for-product>

                    <div class="product__gallery">
                        <product-gallery
                            :gallery-list="gallery"
                            :video-list="videoList"
                            :key="componentKey"
                        >
                        </product-gallery>
                    </div>

                    <div class="product__aside">
                        <product-aside
                            v-if="!product.eol"
                            :product="product"
                            :list-for-header="listForHeader"
                            :in-cart-list="productsInCartIds"
                            @on-scroll-to-anchor="anchorScrollHandler"
                        >
                        </product-aside>
                        <product-aside-eol
                            v-else
                            :product="product"
                            :list-for-header="listForHeader"
                            :products-ids="relatedProductsIds"
                            :slider-breakpoints="breakpoints.related"
                        >
                        </product-aside-eol>
                    </div>
                </div>

                <div class="js-divider-stop"></div>

                <product-body
                    :product="product"
                    :list-for-header="listForHeader"
                    :video-list="videoList"
                    :reviews="reviews"
                    :mobile-active-tab="mobileActiveTabForBody"
                    :top-margin="140"
                >
                    <template #relatedSlider v-if="!product.eol">
                        <product-cards-slider
                            type="related"
                            is-small
                            :number-slides="2"
                            :list-ids="relatedProductsIds"
                            :product-id="product.id"
                            :breakpoints="breakpoints.related"
                            :in-cart-list="productsInCartIds"
                            title="С этим товаром часто покупают"
                        ></product-cards-slider>
                    </template>
                </product-body>

                <product-footer
                    :product="product"
                    :breakpoints="breakpoints.similar"
                    :in-cart-list="productsInCartIds"
                    title="Вам может понравиться"
                >
                    <template #guarantee>
                        <guarantee-list></guarantee-list>
                    </template>
                </product-footer>
            </div>
        </template>
        <div v-else class="product-wrapper__skeleton l-container">
            <the-skeleton v-if="isLoading" type="product-picooc"></the-skeleton>
        </div>
    </div>
</template>

<style lang="scss">
    @import "~@nanoleaf/assets/sass/typography.scss";
    @import "~@nanoleaf/style/ProductNavigation.scss";

    .product {
        @include bp($bp-desktop-sm) {
            margin-top: 50px;
        }
    }

    .product-wrapper__skeleton {
        position: relative;
        max-width: 1260px;
        margin-bottom: 90px;
    }

    .product__head {
        @include bp($bp-desktop-sm) {
            display: flex;
        }
    }

    .product__gallery {
        margin: 0 -16px 34px;
        @include bp($bp-desktop-sm) {
            width: calc(100% - 448px);
            margin: 0 0 34px;
            padding-right: 16px;
        }
    }

    .product__aside {
        @include bp($bp-desktop-sm) {
            flex-shrink: 0;
            width: 448px;
        }
    }

    // ProductCardsSlider
    .product-cards-slider__title {
        margin-bottom: 16px;
        color: var(--neutral-text);
        font-size: 12px;
        font-weight: bold;
        line-height: 20px;
        text-transform: uppercase;
        @include bp($bp-desktop-sm) {
            margin-bottom: 24px;
            font-size: 22px;
            line-height: 30px;
            text-transform: none;
        }
    }

    // ProductFooter
    .product__similar-title {
        margin-bottom: 16px;
        color: var(--neutral-text);
        font-size: 12px;
        font-weight: bold;
        line-height: 20px;
        text-align: left;
        text-transform: uppercase;
        @include bp($bp-desktop-sm) {
            margin-bottom: 24px;
            font-size: 22px;
            line-height: 30px;
            text-transform: none;
        }
    }

    //
    // js
    //

    .product-anchor-style {
        position: relative;
        top: -154px;
        @include bp($bp-desktop-sm) {
            top: -174px;
        }
    }
</style>
